import * as documentPackageHooks from "@rentiohq/shared-frontend/dist/reduxV2/documentPackage/documentPackage.hooks";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  EDocumentPackageStatus,
  EDocumentPackageTypes,
} from "@rentiohq/shared-frontend/dist/types/documentPackage.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import React from "react";
import { ts } from "../../../../services";
import { renderSignersTooltip } from "../../../../utils/documentPackage.utils";
import StatusDot from "../StatusDot";
import { EStatus } from "../StatusDot/StatusDot.types";

interface IProps {
  contract: IContract;
}

const StatusDotContractDocumentPackage = (props: IProps) => {
  const { contract } = props;

  const {
    items: documentPackages,
    isFetching,
    fetchError,
  } = documentPackageHooks.usePaged({
    shouldRefetch: true,
    query: {
      limit: 1,
      page: 1,
      sort: [{ field: "createdAt", method: "DESC" }],
      filter: {
        and: [
          {
            contractId: { eq: contract.id },
          },
          {
            type: { eq: EDocumentPackageTypes.Contract },
          },
          {
            status: {
              notIn: [
                EDocumentPackageStatus.Revoked,
                EDocumentPackageStatus.Expired,
                EDocumentPackageStatus.Failed,
                EDocumentPackageStatus.Rejected,
                EDocumentPackageStatus.WorkerFailed,
              ],
            },
          },
        ],
      },
    },
  });

  // Helpers
  const getStatus = () => {
    if (contract.manuallyCompletedDocumentSigning) {
      return EStatus.Success;
    }

    if (isFetching) {
      return EStatus.None;
    }

    if (!documentPackages) {
      return EStatus.None;
    }

    if (documentPackages.length === 0) {
      return EStatus.Error;
    }

    const documentPackage = documentPackages[0];
    switch (documentPackage.status) {
      case EDocumentPackageStatus.Finished:
        return EStatus.Success;

      default:
        return EStatus.Warning;
    }
  };

  // Render
  const renderTooltipContent = () => {
    if (contract.manuallyCompletedDocumentSigning) {
      return getLocalizedText(contract.manuallyCompletedDocumentSigning);
    }

    if (isFetching) {
      return undefined;
    }

    if (!documentPackages) {
      return undefined;
    }

    if (documentPackages.length === 0) {
      return getLocalizedText("status.signing_not_initiated");
    }

    const documentPackage = documentPackages[0];
    switch (documentPackage.status) {
      case EDocumentPackageStatus.New:
      case EDocumentPackageStatus.Pending:
      case EDocumentPackageStatus.Finished:
        return renderSignersTooltip(documentPackage);

      default:
        return ts.documentPackagesStatus(documentPackage.status);
    }
  };

  React.useEffect(() => {
    if (fetchError) {
      showAlert({ message: fetchError.message, type: "error" });
    }
  }, [fetchError]);

  return (
    <StatusDot status={getStatus()} tooltipContent={renderTooltipContent()} />
  );
};

// eslint-disable-next-line import/no-default-export
export default StatusDotContractDocumentPackage;
